import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {ThreeDots} from '../../../../../../libs/shared/ui/src/components/composer/threeDots';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {TeammatesFormatter} from '../../../../common/formatters/teammatesFormatter';

/*
 * Props.
 */

interface MessagePrecogHeaderProps {
  teammates: ReadonlyArray<TeammateModel>;
  areTeammatesReplying?: boolean;
}

/*
 * Style.
 */

const StyledPrecogDiv = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
`;

const StyledSideDiv = styled.div`
  flex-shrink: 0;
  margin: 8px;
  margin-left: 4px;
`;

const StyledTextDiv = styled.div`
  margin: 8px;
  color: ${(p) => p.theme.greys.shade70};
  ${ellipsis()};
  line-height: normal;
`;

const StyledTeammateSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const MessagePrecogHeader: FC<PropsWithChildren<MessagePrecogHeaderProps>> = (props) => {
  // We need at least one teammate to render.
  const firstTeammate = props.teammates[0];
  if (!firstTeammate) {
    throw new Error('The precog header requires at least one teammate.');
  }

  return (
    <StyledPrecogDiv>
      <StyledSideDiv>
        <TeammateAvatar teammate={firstTeammate} size={VisualSizesEnum.LARGE} />
      </StyledSideDiv>
      <StyledTextDiv>{renderText(props)}</StyledTextDiv>
      {maybeRenderThreeDots(props.areTeammatesReplying)}
      {props.children}
    </StyledPrecogDiv>
  );
};

function renderText(props: MessagePrecogHeaderProps) {
  const {areTeammatesReplying, teammates} = props;
  if (!areTeammatesReplying) {
    return (
      <Trans id="message-draft-seed-many">
        <StyledTeammateSpan>
          <TeammatesFormatter teammates={teammates} />
        </StyledTeammateSpan>{' '}
        started a draft
      </Trans>
    );
  }

  switch (teammates.length) {
    // Is replying.
    case 1:
      return (
        <Trans id="message-precog-one">
          <StyledTeammateSpan>
            <TeammatesFormatter teammates={teammates} />
          </StyledTeammateSpan>{' '}
          is replying
        </Trans>
      );

    // Are replying.
    default:
      return (
        <Trans id="message-precog-many">
          <StyledTeammateSpan>
            <TeammatesFormatter teammates={teammates} />
          </StyledTeammateSpan>{' '}
          are replying
        </Trans>
      );
  }
}

function maybeRenderThreeDots(isReplying?: boolean) {
  if (!isReplying) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledSideDiv>
          <ThreeDots color={theme.greys.shade60} size={VisualSizesEnum.MEDIUM} />
        </StyledSideDiv>
      )}
    </UserThemeConsumer>
  );
}
